import type { NavigationGuard, RouteRecordRedirectOption } from 'vue-router';
import type { FeatureFlagValue } from '@/composables/feature-flags';
import useFeatureFlags from '@/composables/feature-flags';

export function requireFlag(flag: FeatureFlagValue) {
  return ((_to, _from, next) => {
    const { ff } = useFeatureFlags();

    if (!ff(flag)) {
      next('/');
      return;
    }
    next();
  }) satisfies NavigationGuard;
}

/** @lintignore Guard function that may become used/unused depending on which flags we are using */
export function requireDisabledFlag(flag: FeatureFlagValue) {
  return ((_to, _from, next) => {
    const { ff } = useFeatureFlags();

    if (ff(flag)) {
      next('/');
      return;
    }
    next();
  }) satisfies NavigationGuard;
}

export function requireAnyFlag(...flags: FeatureFlagValue[]) {
  return ((_to, _from, next) => {
    const { ff } = useFeatureFlags();

    for (const flag of flags) {
      if (ff(flag)) {
        next();
        return;
      }
    }

    next('/');
  }) satisfies NavigationGuard;
}

interface RedirectOptions {
  to: string;
  defaultTo: string;
}

/**
 * Redirects to a different route based on the feature flag.
 * NOTE: this produces a record `redirect`, which vue-router resolves before any
 * navigation guard — i.e. before flags are loaded. Prefer
 * `defaultChildWithFlag` for flag-dependent redirects.
 * @param flag - The feature flag to check
 * @param options.to - The route to redirect to if the feature flag is enabled
 * @param options.defaultTo - The route to redirect to if the feature flag is not enabled
 * @returns A route record redirect option.
 * @lintignore Guard function that may become used/unused depending on which flags we are using
 */
export function redirectWithFlag(
  flag: FeatureFlagValue,
  options: RedirectOptions,
): RouteRecordRedirectOption {
  return () => {
    const { ff } = useFeatureFlags();
    return { name: ff(flag) ? options.to : options.defaultTo };
  };
}

/**
 * Sends a route on to another while a feature flag is enabled, for a page that
 * has moved behind the flag. A guard rather than a record `redirect`, for the
 * reason given on `defaultChildWithFlag`. Params, query and hash are carried
 * over.
 */
export function redirectWhenFlagged(flag: FeatureFlagValue, to: string): NavigationGuard {
  return (route, _from, next) => {
    const { ff } = useFeatureFlags();

    if (!ff(flag)) {
      next();
      return;
    }
    next({ name: to, params: route.params, query: route.query, hash: route.hash });
  };
}

/**
 * Redirects a parent route to a default child, chosen by a feature flag.
 *
 * Use this instead of `redirectWithFlag` for anything flag-dependent: a record
 * `redirect` is resolved during `router.resolve`, before any navigation guard
 * runs, and therefore before the global `beforeEach` in `routes/index.ts` has
 * awaited `setFlags()`. On a cold load it would read an empty flag store. A
 * guard runs after that `beforeEach`, so it sees real flags.
 *
 * Passes through untouched unless the resolved route *is* the parent, so child
 * routes are unaffected. Params, query and hash are carried over, matching what
 * a record `redirect` does.
 */
export function defaultChildWithFlag(
  parentName: string,
  flag: FeatureFlagValue,
  options: RedirectOptions,
): NavigationGuard {
  return (to, _from, next) => {
    if (to.name !== parentName) {
      next();
      return;
    }

    const { ff } = useFeatureFlags();
    next({
      name: ff(flag) ? options.to : options.defaultTo,
      params: to.params,
      query: to.query,
      hash: to.hash,
    });
  };
}
